import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { LinkIcon, Grip, PAGE_ICONS, hasGlyph, shortcutGlyph } from '../components/links/icons';
import * as L from '../lib/links';
import { relative } from '../lib/when';
import { storedViewAsContractorId } from '../lib/viewAs';
import { ExtrasContext, StarButton, StarGlyph, RatesCard, RatesHit, FavouritesView, FAV_SLUG, indexLinks, useLinksExtras } from '../components/links/LinksExtras';
import './LinksPage.css';

/**
 * Links: the firm's shelf of websites and apps (Gordon, 22 October).
 *
 * "A new page for all users which will contain links to websites & apps
 * categorised by nature" - the Windows calculator and snipping tool, the
 * professional sites, and separately the news, the radio, the sport and the
 * weather - which staff can add to and take from, with a record of who did
 * what and a way back.
 *
 * PAGES down the left (a strip of tabs on a phone), the open page's SECTIONS
 * as cards, its LINKS as rows in them. A web link opens in a new tab with
 * noopener; an app link (ms-calculator:, shortcuts:) opens the app.
 *
 * WINDOWS OR MAC (Gordon, 22 October: "Windows apps visible only on Windows,
 * Mac apps visible only on a Mac"). Every link says where it shows - any,
 * windows or mac (migration 075) - and the page shows a Windows link only on
 * Windows and a Mac link only on a Mac. A phone, a tablet (an iPad included,
 * though it calls itself a Mac) and a Linux computer get neither: no Windows
 * or Mac app opens there. Counts count what is shown; a section, or a page,
 * left with nothing to show here is not shown, and a line under the page
 * says how many links are for other computers. In edit mode staff see
 * everything, each platform-only link badged, so they can look after it.
 *
 * A SHORTCUT is a tile that opens nothing: a Mac app no web page can open
 * (Calculator, Screenshot) shows the keys that open it, as key caps, and a
 * press of the tile opens a small "how to open" card with the steps.
 *
 * Search looks across every page at once - nobody should need to know that
 * the exchange rates are under Professional to find them.
 *
 * EDITING is for staff and administrators: the pencil turns the page into
 * its own editor - add, rename and delete pages, sections and links, drag
 * them into order (or focus a handle and use the arrow keys). A delete
 * happens at once and offers Undo, which is the same Restore as Recently
 * deleted. History is the server's record of every change. A contractor gets
 * the page and none of the controls (routes/links.js refuses their writes
 * whatever the page shows), and so does an administrator looking through a
 * contractor's eyes with View as - that is what the contractor sees.
 */

const UNDO_MS = 8000;
const TONES = ['blue', 'teal', 'green', 'purple', 'orange', 'pink', 'red', 'yellow'];
const toneOf = (s) => TONES[[...String(s || '')].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % TONES.length];
const initialsOf = (title) => {
  const words = String(title || '').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};
const originOf = (url) => { try { return new URL(url).origin; } catch { return null; } };
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const LAST_PAGE_KEY = 'jarvis-links-page';
const remembered = () => { try { return localStorage.getItem(LAST_PAGE_KEY) || null; } catch { return null; } };
const remember = (slug) => { try { localStorage.setItem(LAST_PAGE_KEY, slug); } catch { /* private window */ } };

/*
 * THE REORGANISATION (migration 082, 23 October): eight pages by service
 * line under three captions, regulators as authority cards, a line under
 * each section. Everything below reads what the server sends and falls back
 * to the page as it was when it sends none of it (before 082).
 */

/** The captions in the left nav, in order; a page with no group goes under "More". */
const GROUPS = [['everyday', 'Everyday'], ['work', 'Work'], ['leisure', 'Off duty']];
const GROUP_WORD = Object.fromEntries(GROUPS);
/** The two pages 082 retired, and the page each one's bookmark now opens. */
const RETIRED_SLUGS = { 'online-tools': 'tools', 'events-and-calendars': 'sport' };
/** "Accounting & audit" is "Accounting" on a phone's strip. */
const shortName = (title) => String(title || '').split(/\s*(?:&|,)\s*/)[0] || title;
/** Titles are stored with a plain " - " and typeset with an en dash. */
const dash = (s) => (s ? String(s).replace(/ - /g, ' – ') : s);

const DENSITY_KEY = 'jarvis-links-density';
const savedDensity = () => { try { return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable'; } catch { return 'comfortable'; } };
const saveDensity = (d) => { try { localStorage.setItem(DENSITY_KEY, d); } catch { /* private window */ } };

/** A link that names a year - a rate table, a calendar, a handbook - wants checking each January. */
// In the name, a year from this decade or the next - not a directive's number (2006/112).
const yearOf = (link) => (/\b(20[23]\d)\b(?!\/)/.exec(link.title || '') || [])[1] || null;

/**
 * What people type for what the shelf calls something else: the old names of
 * the tax and company registries, the office everybody still calls ETC.
 */
const SYNONYMS = { cfr: ['mtca'], ird: ['mtca'], roc: ['mbr'], registry: ['mbr'], etc: ['jobsplus'],
  ssc: ['social security'], ni: ['social security'], aml: ['fiau'], mlro: ['fiau'], str: ['suspicious'] };
/** A query as groups of words, each satisfied by any of its alternatives. */
function searchTerms(query) {
  let q = ` ${String(query || '').toLowerCase().trim()} `;
  const groups = [];
  if (/\svat numbers?\s/.test(q)) { groups.push(['vies', 'vat number']); q = q.replace(/\svat numbers?\s/g, ' '); }
  for (const w of q.split(/\s+/).filter(Boolean)) groups.push([w, ...(SYNONYMS[w] || [])]);
  return groups;
}

/* =============================================================== dragging */

/**
 * Dragging a thing into place among its siblings, by pointer: mouse and
 * finger are one code path. The column (or grid) is the nearest
 * `[data-lk-order]` above the handle and its items are the children carrying
 * `data-lk-item`. Works for a list and for the grid of section cards: in a
 * grid an item is "before" the pointer if its row is above it, or it is in
 * the pointer's row and to its left. Escape cancels. `onDrop(group, from, to)`
 * gets `to` as the item's final index.
 */
function useDragOrder(onDrop) {
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  const start = (e, group, from) => {
    if (ref.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const box = e.currentTarget.closest('[data-lk-order]');
    if (!box) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* synthetic event */ }
    ref.current = { group, from, to: from, box, pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, started: false };
    setDrag({ group, from, to: from, started: false });
  };

  const active = Boolean(drag);
  useEffect(() => {
    if (!active) return undefined;
    const othersOf = (d) => [...d.box.children].filter((el) => el.hasAttribute('data-lk-item')).filter((_, i) => i !== d.from);
    const onMove = (e) => {
      const d = ref.current;
      if (!d || e.pointerId !== d.pointerId) return;
      if (!d.started) {
        if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 4) return;
        d.started = true;
        document.body.classList.add('lk-dragging');
      }
      e.preventDefault();
      const others = othersOf(d);
      const rects = others.map((el) => el.getBoundingClientRect());
      const oneColumn = rects.every((r) => Math.abs(r.left - (rects[0]?.left ?? 0)) < 4);
      const x = e.clientX; const y = e.clientY;
      const to = rects.filter((r) => (oneColumn
        ? r.top + r.height / 2 < y
        : r.bottom < y || (r.top <= y && y <= r.bottom && r.left + r.width / 2 < x))).length;
      if (to !== d.to || !d.shown) {
        d.to = to; d.shown = true;
        setDrag({ group: d.group, from: d.from, to, started: true });
      }
    };
    const finish = (commit) => {
      const d = ref.current;
      ref.current = null;
      document.body.classList.remove('lk-dragging');
      setDrag(null);
      if (commit && d && d.started && d.to !== d.from) dropRef.current(d.group, d.from, d.to);
    };
    const onUp = (e) => { if (ref.current && e.pointerId === ref.current.pointerId) finish(true); };
    const onCancel = (e) => { if (ref.current && e.pointerId === ref.current.pointerId) finish(false); };
    const onKey = (e) => { if (e.key === 'Escape') finish(false); };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [active]);

  /** Extra classes for item `i` of `total`: the one moving, and where it would land. */
  const markOf = (group, i, total) => {
    if (!drag || !drag.started || drag.group !== group) return '';
    if (i === drag.from) return ' is-dragging';
    const j = i > drag.from ? i - 1 : i;
    if (j === drag.to) return ' is-drop-before';
    if (drag.to === total - 1 && j === total - 2) return ' is-drop-after';
    return '';
  };

  return { start, markOf };
}

/** The handle: drag it, or focus it and press the arrow keys. */
function Handle({ label, group, index, total, drag, onMove, id }) {
  return (
    <button type="button" className="lk-handle" data-handle={id}
            aria-label={`Move ${label} - drag, or use the arrow keys`}
            title="Drag to move, or use the arrow keys"
            onPointerDown={(e) => drag.start(e, group, index)}
            onKeyDown={(e) => {
              const up = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
              const down = e.key === 'ArrowDown' || e.key === 'ArrowRight';
              if (!up && !down) return;
              e.preventDefault();
              const to = up ? index - 1 : index + 1;
              if (to < 0 || to >= total) return;
              onMove(group, index, to, id);
            }}>
      <Grip />
    </button>
  );
}

/* ============================================================ right-click */

/**
 * Right-click on a web link (Gordon, 22 October: "is it possible to open
 * links in new tab/windows? using right click"). The browser's own menu is
 * not the same everywhere - the installed Jarvis window shows a short one -
 * so a link gets Jarvis's menu: Open, Open in new tab, Open in new window,
 * Copy link address. Shift + right-click still gives the browser's menu, and
 * Ctrl-click and middle-click still open a tab, because the row is a real
 * link.
 *
 * Every way out keeps noopener and noreferrer: the site opened never gets a
 * handle on this window, nor learns which Jarvis page it came from.
 */
export function openLink(url, how) {
  if (how === 'window') {
    // A window of its own, a little inside the screen this one is on.
    const w = Math.min(1280, Math.round(window.screen.availWidth * 0.8));
    const h = Math.min(900, Math.round(window.screen.availHeight * 0.85));
    const left = Math.round((window.screenX || 0) + Math.max(0, (window.outerWidth - w) / 2));
    const top = Math.round((window.screenY || 0) + Math.max(0, (window.outerHeight - h) / 2));
    window.open(url, '_blank', `popup,noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const MenuContext = React.createContext(null);

function LinkMenu({ menu, onClose, onCopied }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: menu.x, top: menu.y });

  // Inside the window whichever corner it was opened near.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 8;
    setPos({
      left: Math.max(pad, Math.min(menu.x, window.innerWidth - r.width - pad)),
      top: Math.max(pad, Math.min(menu.y, window.innerHeight - r.height - pad))
    });
    el.querySelector('button')?.focus();
  }, [menu]);

  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(false); };
    const key = (e) => {
      if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); onClose(true); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const items = [...ref.current.querySelectorAll('button')];
      const i = items.indexOf(document.activeElement);
      const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1
        : (i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[next].focus();
    };
    const gone = () => onClose(false);
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', gone);
    window.addEventListener('blur', gone);
    document.addEventListener('scroll', gone, true);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', gone);
      window.removeEventListener('blur', gone);
      document.removeEventListener('scroll', gone, true);
    };
  }, [onClose]);

  const { link } = menu;
  const ex = React.useContext(ExtrasContext);
  const pick = (fn) => () => { onClose(true); fn(); };
  const open = (how) => pick(() => { ex?.record(link.id); openLink(link.url, how); });
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); onCopied(true); } catch { onCopied(false); }
  };
  return (
    <div ref={ref} className="lk-menu" role="menu" aria-label={link.title} style={pos}
         onContextMenu={(e) => e.preventDefault()}>
      <div className="lk-menu-head" aria-hidden="true">{L.hostOf(link.url)}</div>
      <button type="button" role="menuitem" onClick={open('tab')}>
        <LinkIcon name="external" size={15} /> Open
      </button>
      <button type="button" role="menuitem" onClick={open('tab')}>
        <LinkIcon name="tab" size={15} /> Open in new tab
      </button>
      <button type="button" role="menuitem" onClick={open('window')}>
        <LinkIcon name="window" size={15} /> Open in new window
      </button>
      <span className="lk-menu-rule" role="separator" />
      <button type="button" role="menuitem" onClick={pick(copy)}>
        <LinkIcon name="copy" size={15} /> Copy link address
      </button>
      {ex?.ready && (
        <button type="button" role="menuitem" onClick={pick(() => ex.toggle(link))}>
          <StarGlyph filled={ex.isFav(link.id)} size={15} /> {ex.isFav(link.id) ? 'Remove from favourites' : 'Add to favourites'}
        </button>
      )}
    </div>
  );
}

/* ================================================================= pieces */

/**
 * The site's own icon, from its /favicon.ico - never a third-party favicon
 * service, which would tell somebody else every site on the firm's shelf.
 * Initials on a soft colour until it loads, and instead of it if it does not.
 */
function Favicon({ link, scheme }) {
  const [state, setState] = useState('loading');
  const origin = link.kind === 'web' ? originOf(link.url) : null;
  if (link.kind === 'shortcut') {
    return (
      <span className="lk-fav is-app is-keys" aria-hidden="true">
        <LinkIcon name={shortcutGlyph(link.title)} size={18} />
      </span>
    );
  }
  if (link.kind === 'app') {
    return (
      <span className="lk-fav is-app" aria-hidden="true">
        <LinkIcon name={hasGlyph(scheme) ? scheme : 'link'} size={18} />
      </span>
    );
  }
  return (
    <span className={`lk-fav lk-tone-${toneOf(link.title)}${state === 'ok' ? ' has-icon' : ''}`} aria-hidden="true"
          data-icon={origin ? `${origin}/favicon.ico` : undefined}>
      {state !== 'ok' && <span className="lk-fav-initials">{initialsOf(link.title)}</span>}
      {origin && state !== 'failed' && (
        <img src={`${origin}/favicon.ico`} alt="" loading="lazy" referrerPolicy="no-referrer" decoding="async"
             onLoad={(e) => setState(e.currentTarget.naturalWidth > 4 ? 'ok' : 'failed')}
             onError={() => setState('failed')} />
      )}
    </span>
  );
}

const PLATFORM_WORD = { windows: 'Windows', mac: 'Mac' };

/**
 * A shortcut's keys as key caps: ⌘ ⇧ 5, or ⌘ Space → Calculator → ↩.
 * `compact` is the line under a tile's name; otherwise each step is a line
 * of the "how to open" card, saying what to do.
 */
function Keys({ keys, platform, compact = false }) {
  const steps = L.parseKeys(keys);
  if (compact) {
    return (
      <span className="lk-keys" aria-hidden="true">
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="lk-keys-then">›</span>}
            {s.type !== undefined
              ? <span className="lk-typed">{s.type}</span>
              : s.press.map((k, j) => <kbd key={j} className="lk-cap">{L.keyCap(k, platform)[0]}</kbd>)}
          </React.Fragment>
        ))}
      </span>
    );
  }
  return (
    <ol className={`lk-steps${steps.length === 1 ? ' is-single' : ''}`}>
      {steps.map((s, i) => (
        <li key={i} className="lk-step">
          {steps.length > 1 && <span className="lk-step-n" aria-hidden="true">{i + 1}</span>}
          {s.type !== undefined ? (
            <span className="lk-step-body">Type <q className="lk-typed is-big">{s.type}</q></span>
          ) : (
            <span className="lk-step-body">
              {steps.length === 1 && s.press.length > 1 ? 'Press these together' : 'Press'}
              <span className="lk-caps">
                {s.press.map((k, j) => {
                  const [cap, word] = L.keyCap(k, platform);
                  return (
                    <React.Fragment key={j}>
                      {j > 0 && <span className="lk-plus" aria-hidden="true">+</span>}
                      <kbd className="lk-cap is-big" title={word}>
                        {cap}{cap !== word && cap.length <= 2 && <span className="lk-cap-word">{word}</span>}
                      </kbd>
                    </React.Fragment>
                  );
                })}
              </span>
              {platform === 'mac' && s.press.join('+') === 'cmd+space' && <span className="lk-step-hint"> - opens Spotlight</span>}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

/** The badge on an app link: where it opens, or what it is (Email). */
function AppBadge({ app }) {
  if (!app) return null;
  const where = app.platform || (app.windows ? 'windows' : 'any');
  if (where === 'any') return <span className="lk-badge">{app.label}</span>;
  return (
    <span className={`lk-badge is-${where}`}>
      <LinkIcon name={where === 'windows' ? 'windows' : 'mac'} size={11} />{PLATFORM_WORD[where]} app
    </span>
  );
}

const PopContext = React.createContext(null);

/**
 * Jarvis's own right-click menu on a web link (Shift for the browser's), from
 * the pointer or - from the keyboard's menu key - under the link.
 */
const menuOn = (openMenu, link) => (e) => {
  if (e.shiftKey || !openMenu || link.kind !== 'web') return;
  e.preventDefault();
  const r = e.currentTarget.getBoundingClientRect();
  const fromKeys = e.clientX === 0 && e.clientY === 0;
  openMenu({ link, x: fromKeys ? r.left + 24 : e.clientX, y: fromKeys ? r.bottom : e.clientY, from: e.currentTarget });
};

/**
 * One link: a row that opens it, or in edit mode a row that edits it.
 * `name` is what it is called out of its card ("FIAU · Implementing
 * Procedures" in Favourites), `where` the line saying where it lives.
 */
function Tile({ link, schemes, editing, onEdit, onDelete, handle, mark = '', where = null, name = null }) {
  const scheme = L.schemeOf(link.url);
  const app = link.kind === 'app' ? schemes.find((s) => s.scheme === scheme) : null;
  const platform = L.platformOf(link, schemes);
  const shortcut = link.kind === 'shortcut';
  const sub = shortcut ? null : dash(link.description) || (link.kind === 'web' ? L.hostOf(link.url) : app?.label || '');
  const openMenu = React.useContext(MenuContext);
  const openPop = React.useContext(PopContext);
  const year = editing ? yearOf(link) : null;
  const body = (
    <>
      <Favicon link={link} scheme={scheme} />
      <span className="lk-tile-text">
        <span className="lk-tile-title">{editing ? link.title : dash(name || link.title)}</span>
        {(sub || where || shortcut) && (
          <span className="lk-tile-sub">
            {where ? `${dash(where)}${sub || shortcut ? ' · ' : ''}` : ''}
            {shortcut ? <Keys keys={link.keys} platform={platform} compact /> : sub}
          </span>
        )}
      </span>
      {year && (
        <span className="lk-year" title={`Names ${year} - check it each January`}>
          {year}<span className="lk-sr"> - check it each January</span>
        </span>
      )}
      {editing
        ? platform !== 'any' && (
          // Just the mark, so the name keeps its room; the words are in the
          // tooltip and for a screen reader.
          <span className={`lk-plat is-icon is-${platform}`} title={`Shown only on ${platform === 'mac' ? 'a Mac' : 'Windows'}`}>
            <LinkIcon name={platform === 'windows' ? 'windows' : 'mac'} size={12} />
            <span className="lk-sr">{PLATFORM_WORD[platform]} only</span>
          </span>
        )
        : <AppBadge app={app} />}
    </>
  );

  if (editing) {
    return (
      <li className={`lk-tile is-editing${mark}`} data-lk-item="">
        {handle}
        <button type="button" className="lk-tile-main" onClick={onEdit} aria-label={`Edit ${link.title}`}>{body}</button>
        <span className="lk-tile-tools">
          <button type="button" className="lk-icon-btn" onClick={onEdit} aria-label={`Edit ${link.title}`} title="Edit">
            <LinkIcon name="pencil" />
          </button>
          <button type="button" className="lk-icon-btn is-danger" onClick={onDelete} aria-label={`Delete ${link.title}`} title="Delete">
            <LinkIcon name="trash" />
          </button>
        </span>
      </li>
    );
  }
  if (shortcut) {
    // Not a link, and it does not look like one: a button that shows the
    // keys, with a keyboard where a link has its arrow.
    return (
      <li className="lk-tile" data-lk-item="" data-link-id={link.id}>
        <button type="button" className="lk-tile-main is-keys" aria-haspopup="dialog"
                aria-label={`${link.title} - how to open it: ${L.keysInWords(link.keys, platform)}`}
                title={link.description || `How to open ${link.title}`}
                onClick={(e) => openPop?.({ link, platform, from: e.currentTarget })}>
          {body}
          <span className="lk-go is-keys" aria-hidden="true"><LinkIcon name="keyboard" size={15} /></span>
        </button>
        <StarButton link={link} />
      </li>
    );
  }
  return (
    <li className="lk-tile" data-lk-item="" data-link-id={link.id}>
      {link.kind === 'web' ? (
        <a className="lk-tile-main" href={link.url} target="_blank" rel="noopener noreferrer"
           title={link.description ? `${dash(link.title)} – ${dash(link.description)}` : dash(link.title)}
           onContextMenu={menuOn(openMenu, link)}>
          {body}
          <span className="lk-go" aria-hidden="true"><LinkIcon name="external" size={14} /></span>
        </a>
      ) : (
        // An app opens in place: a new tab for ms-calculator: would be a
        // blank tab left behind every time.
        <a className="lk-tile-main" href={link.url} title={`Open ${app?.label || link.title}`}>{body}</a>
      )}
      <StarButton link={link} />
    </li>
  );
}

/**
 * The "how to open" card under a shortcut tile: the steps, the description,
 * and why there is no link. Escape, a click elsewhere or scrolling closes it,
 * and the focus goes back to the tile.
 */
function ShortcutPop({ pop, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const { link, platform, from } = pop;

  useEffect(() => {
    const el = ref.current;
    if (!el || !from) return;
    const place = () => {
      const a = from.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const pad = 8;
      const below = a.bottom + 6;
      const top = below + r.height + pad > window.innerHeight && a.top - r.height - 6 > pad ? a.top - r.height - 6 : below;
      setPos({ left: Math.max(pad, Math.min(a.left, window.innerWidth - r.width - pad)), top: Math.max(pad, top) });
    };
    place();
    el.querySelector('.lk-pop-close')?.focus();
  }, [from]);

  useEffect(() => {
    const away = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !from?.contains(e.target)) onClose(false);
    };
    const key = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(true); }
      if (e.key === 'Tab') {
        // Two things in the card: keep the focus in it, as a dialog does.
        const items = [...ref.current.querySelectorAll('button')];
        if (items.length < 2) { e.preventDefault(); return; }
        const i = items.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); items[items.length - 1].focus(); }
        if (!e.shiftKey && i === items.length - 1) { e.preventDefault(); items[0].focus(); }
      }
    };
    const gone = () => onClose(false);
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', gone);
    document.addEventListener('scroll', gone, true);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', gone);
      document.removeEventListener('scroll', gone, true);
    };
  }, [onClose, from]);

  const titleId = `lk-pop-${link.id}`;
  const os = platform === 'windows' ? 'Windows' : 'macOS';
  return (
    <div ref={ref} className="lk-pop" role="dialog" aria-modal="false" aria-labelledby={titleId}
         style={pos || { left: -9999, top: 0 }}>
      <div className="lk-pop-head">
        <span className="lk-fav is-app is-keys" aria-hidden="true"><LinkIcon name={shortcutGlyph(link.title)} size={18} /></span>
        <div className="lk-pop-titles">
          <p className="lk-pop-kicker">How to open</p>
          <h3 className="lk-pop-title" id={titleId}>{link.title}</h3>
        </div>
        <button type="button" className="lk-icon-btn lk-pop-close" aria-label="Close" onClick={() => onClose(true)}>
          <LinkIcon name="close" size={14} />
        </button>
      </div>
      <Keys keys={link.keys} platform={platform} />
      {link.description && <p className="lk-pop-desc">{link.description}</p>}
      <p className="lk-pop-note">
        <LinkIcon name="info" size={13} /> {os} does not let a web page open this, so here are the keys instead.
      </p>
    </div>
  );
}

/**
 * A chip in an authority card: one of the regulator's deep links, by its
 * short name. A real link all the same - starred, counted in Most used, with
 * the right-click menu - its description in the tooltip and for a screen
 * reader.
 */
function Chip({ link, schemes }) {
  const openMenu = React.useContext(MenuContext);
  const openPop = React.useContext(PopContext);
  const platform = L.platformOf(link, schemes);
  const label = dash(link.title);
  const about = dash(link.description) || (link.kind === 'web' ? L.hostOf(link.url) : '');
  const inner = (
    <>
      <span className="lk-chip-text">{label}</span>
      {about && link.kind !== 'shortcut' && <span className="lk-sr"> - {about}</span>}
    </>
  );
  return (
    <li className="lk-chip-item" data-link-id={link.id}>
      {link.kind === 'shortcut' ? (
        <button type="button" className="lk-chip-link is-keys" aria-haspopup="dialog" title={about || `How to open ${label}`}
                aria-label={`${label} - how to open it: ${L.keysInWords(link.keys, platform)}`}
                onClick={(e) => openPop?.({ link, platform, from: e.currentTarget })}>{inner}</button>
      ) : link.kind === 'web' ? (
        <a className="lk-chip-link" href={link.url} target="_blank" rel="noopener noreferrer"
           title={about ? `${label} – ${about}` : label}
           onContextMenu={menuOn(openMenu, link)}>{inner}</a>
      ) : (
        <a className="lk-chip-link" href={link.url} title={about || label}>{inner}</a>
      )}
      <StarButton link={link} />
    </li>
  );
}

/**
 * A regulator's card (style 'authority', migration 082): its main site large
 * at the top - favicon, name, the section's one line - and its deep links as
 * chips below. In edit mode it is an ordinary list again, so its links can be
 * dragged, edited and moved in and out like any others.
 */
function AuthorityCard({ section, schemes }) {
  const openMenu = React.useContext(MenuContext);
  const [main, ...rest] = section.links;
  const scheme = L.schemeOf(main.url);
  const host = main.kind === 'web' ? L.hostOf(main.url) : '';
  const line = dash(section.description || main.description);
  const titleId = `lk-sec-${section.id}`;
  return (
    <section className={`lk-card lk-auth${rest.length >= 7 ? ' is-wide' : ''}`} data-lk-item="" aria-labelledby={titleId}>
      <div className="lk-auth-head" data-link-id={main.id}>
        <a className="lk-auth-main" href={main.url}
           {...(main.kind === 'web' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
           title={main.description ? `${dash(main.title)} – ${dash(main.description)}` : dash(main.title)}
           onContextMenu={menuOn(openMenu, main)}>
          <Favicon link={main} scheme={scheme} />
          <span className="lk-auth-text">
            <h3 className="lk-auth-title" id={titleId}>
              {dash(section.title)}
              {main.title !== section.title && <span className="lk-sr"> - {main.title}</span>}
            </h3>
            {line && <span className="lk-auth-line">{line}</span>}
            {host && (
              <span className="lk-auth-host">{host}<LinkIcon name="external" size={11} /></span>
            )}
          </span>
        </a>
        <StarButton link={main} />
      </div>
      {rest.length > 0 && (
        <ul className="lk-chips" aria-label={`More from ${section.title}`}>
          {rest.map((l) => <Chip key={l.id} link={l} schemes={schemes} />)}
        </ul>
      )}
    </section>
  );
}

/** A section: its title, its line, its links, and in edit mode the tools for all three. */
function SectionCard({ section, index, total, editing, drag, onMove, schemes, act }) {
  const group = `links:${section.id}`;
  const count = section.links.length;
  if (!editing && section.style === 'authority' && count > 0) {
    return <AuthorityCard section={section} schemes={schemes} />;
  }
  // In edit mode: a section whose every link is for one platform says so,
  // because everybody else may never see it.
  const only = editing && count > 0 ? (() => {
    const ps = new Set(section.links.map((l) => L.platformOf(l, schemes)));
    return ps.size === 1 && !ps.has('any') ? [...ps][0] : null;
  })() : null;
  return (
    <section className={`lk-card${editing ? ' is-editing' : ''}${drag.markOf('sections', index, total)}`}
             data-lk-item="" aria-labelledby={`lk-sec-${section.id}`}>
      <header className="lk-card-head">
        {editing && <Handle label={section.title} group="sections" index={index} total={total} drag={drag} onMove={onMove} id={section.id} />}
        <h3 className="lk-card-title" id={`lk-sec-${section.id}`}>{editing ? section.title : dash(section.title)}</h3>
        {editing && section.style === 'authority' && (
          <span className="lk-style-badge" title="Shown as an authority card: the first link large, the rest as chips">Card</span>
        )}
        <span className="lk-count" aria-label={plural(count, 'link')}>{count}</span>
        {editing && (
          <span className="lk-card-tools">
            <button type="button" className="lk-icon-btn" disabled={index === 0} onClick={() => onMove('sections', index, index - 1, section.id)}
                    aria-label={`Move ${section.title} up`} title="Move up"><LinkIcon name="up" /></button>
            <button type="button" className="lk-icon-btn" disabled={index === total - 1} onClick={() => onMove('sections', index, index + 1, section.id)}
                    aria-label={`Move ${section.title} down`} title="Move down"><LinkIcon name="down" /></button>
            <button type="button" className="lk-icon-btn" onClick={() => act.renameSection(section)}
                    aria-label={`Rename ${section.title}`} title="Rename"><LinkIcon name="pencil" /></button>
            <button type="button" className="lk-icon-btn is-danger" onClick={() => act.deleteSection(section)}
                    aria-label={`Delete ${section.title}`} title="Delete section"><LinkIcon name="trash" /></button>
          </span>
        )}
      </header>
      {section.description && (
        <p className="lk-card-desc" title={dash(section.description)}>{dash(section.description)}</p>
      )}
      {only && (
        <p className={`lk-card-only is-${only}`}>
          <span className={`lk-plat is-${only}`}>
            <LinkIcon name={only === 'windows' ? 'windows' : 'mac'} size={11} />{PLATFORM_WORD[only]} only
          </span>
          Everything here shows only on {only === 'mac' ? 'a Mac' : 'Windows'}.
        </p>
      )}
      {count === 0 && !editing && <p className="lk-empty-line">Nothing here yet.</p>}
      {count === 0 && editing && (
        <p className="lk-empty-line is-editing">No links here yet - add one below, or move one in from its Edit dialog.</p>
      )}
      <ul className="lk-tiles" data-lk-order="">
        {section.links.map((link, i) => (
          <Tile key={link.id} link={link} schemes={schemes} editing={editing}
                mark={drag.markOf(group, i, count)}
                onEdit={() => act.editLink(section, link)} onDelete={() => act.deleteLink(link)}
                handle={editing && <Handle label={link.title} group={group} index={i} total={count} drag={drag} onMove={onMove} id={link.id} />} />
        ))}
      </ul>
      {editing && (
        <button type="button" className="lk-add-row" onClick={() => act.addLink(section)}>
          <LinkIcon name="plus" /> Add a link
        </button>
      )}
    </section>
  );
}

/* ================================================================ dialogs */

/**
 * Add, rename or (after 082) describe a page or a section. `more` says what
 * else this server can store: a section's one-line description and whether it
 * is an authority card, a page's group in the left nav.
 */
function NameDialog({ what, initial, withIcon, more = {}, onSave, onClose }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [icon, setIcon] = useState(initial?.icon || 'link');
  const [description, setDescription] = useState(initial?.description || '');
  const [style, setStyle] = useState(initial?.style || 'list');
  const [group, setGroup] = useState(initial ? (initial.nav_group || '') : (more.defaultGroup || ''));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const max = what === 'page' ? 60 : 80;
  const describe = what === 'section' && more.sections;
  const grouped = what === 'page' && more.groups;
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError(`Give the ${what} a name`); return; }
    setBusy(true); setError(null);
    try {
      await onSave({ title: title.trim(), ...(withIcon ? { icon } : {}),
        ...(describe ? { description: description.trim() || null, style } : {}),
        ...(grouped ? { nav_group: group || null } : {}) });
    } catch (err) {
      setError(L.said(err, `Could not save the ${what}`)); setBusy(false);
    }
  };
  const long = description.trim().length > 80;
  return (
    <Modal title={initial ? `${describe || grouped ? 'Edit' : 'Rename'} ${what}` : `Add a ${what}`} onClose={onClose}
           footer={(
             <>
               <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
               <button type="submit" form="lk-name-form" className="btn btn-primary" disabled={busy}>
                 {initial ? 'Save' : `Add ${what}`}
               </button>
             </>
           )}>
      <form id="lk-name-form" className="lk-form" onSubmit={submit}>
        <label className="lk-field">
          <span className="lk-label">Name</span>
          <input type="text" name="title" value={title} maxLength={max} autoFocus onChange={(e) => setTitle(e.target.value)}
                 placeholder={what === 'page' ? 'e.g. Client portals' : 'e.g. Banks'} />
        </label>
        {describe && (
          <>
            <label className="lk-field">
              <span className="lk-label">One line under the name <span className="lk-optional">optional</span></span>
              <input type="text" name="description" value={description} maxLength={160}
                     onChange={(e) => setDescription(e.target.value)} placeholder="What these links are for" />
              <span className={`lk-counter${long ? ' is-long' : ''}`} aria-live="polite">
                {description.trim().length}/80{long ? ' - it may be cut short on the page' : ''}
              </span>
            </label>
            <fieldset className="lk-field">
              <legend className="lk-label">Show it as</legend>
              <div className="lk-seg lk-style-seg" role="radiogroup" aria-label="Show it as">
                {[['list', 'A list of links'], ['authority', 'An authority card']].map(([v, label]) => (
                  <button key={v} type="button" role="radio" aria-checked={style === v} className={style === v ? 'is-on' : ''}
                          onClick={() => setStyle(v)}>{label}</button>
                ))}
              </div>
              <span className="lk-help">
                {style === 'authority'
                  ? 'For a regulator or authority: its first link is shown large, as the main site, and the others as chips under it.'
                  : 'Every link a row, with its description.'}
              </span>
            </fieldset>
          </>
        )}
        {grouped && (
          <label className="lk-field">
            <span className="lk-label">In the list under</span>
            <select name="nav_group" value={group} onChange={(e) => setGroup(e.target.value)}>
              {GROUPS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              <option value="">No heading</option>
            </select>
          </label>
        )}
        {withIcon && (
          <fieldset className="lk-field lk-icons">
            <legend className="lk-label">Icon</legend>
            <div className="lk-icon-grid">
              {PAGE_ICONS.map((n) => (
                <button key={n} type="button" className={`lk-icon-pick${icon === n ? ' is-on' : ''}`}
                        aria-pressed={icon === n} aria-label={n} title={n} onClick={() => setIcon(n)}>
                  <LinkIcon name={n} size={18} />
                </button>
              ))}
            </div>
          </fieldset>
        )}
        {error && <p className="lk-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

const SHOW_ON = [['any', 'Every computer'], ['windows', 'Windows only'], ['mac', 'Mac only']];

/**
 * Add or edit a link: a web address or an app, or (075) the keys that open
 * an app no web page can - and where it shows. An app's "Show on" is its
 * scheme's and is shown fixed; keys are for Windows or for a Mac, never both.
 * Before 075 (`features` false) the dialog is what it was.
 */
function LinkDialog({ initial, preset = null, pickSection = false, section, pages, schemes, features, onSave, onClose }) {
  const [kind, setKind] = useState(initial?.kind === 'shortcut' ? 'shortcut' : 'link');
  const [url, setUrl] = useState(initial?.url || '');
  const [keys, setKeys] = useState(initial?.keys || '');
  const [title, setTitle] = useState(initial?.title || preset?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [platform, setPlatform] = useState(initial ? L.platformOf(initial, schemes) : 'any');
  const [sectionId, setSectionId] = useState(section.id);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const page = pages.find((p) => p.sections.some((s) => s.id === section.id));
  const canPlatform = Boolean(features?.platforms);
  const canKeys = Boolean(features?.shortcuts);

  // An app that opens in one place decides where it shows.
  const app = kind === 'link' ? schemes.find((s) => s.scheme === L.schemeOf(url)) : null;
  const fixed = app ? (app.platform || (app.windows ? 'windows' : 'any')) : 'any';
  const locked = kind === 'link' && fixed !== 'any';
  const shownOn = locked ? fixed : (kind === 'shortcut' && platform === 'any' ? 'mac' : platform);

  const pickApp = (a) => {
    setUrl(`${a.scheme}:${a.scheme === 'shortcuts' || a.scheme === 'macappstore' || a.scheme === 'music' ? '//' : ''}`);
    if (!title.trim() || schemes.some((s) => s.label === title)) setTitle(a.label);
  };
  const submit = async (e) => {
    e.preventDefault();
    let body;
    if (kind === 'shortcut') {
      if (!keys.trim()) { setError('Say which keys open it - for example cmd+shift+5'); return; }
      if (!title.trim()) { setError('Give it a name - the app the keys open'); return; }
      body = { kind: 'shortcut', keys: keys.trim(), title: title.trim(), platform: shownOn };
    } else {
      const tidy = L.tidyUrl(url);
      if (!tidy) { setError('Give the link a web address'); return; }
      const name = title.trim() || L.hostOf(tidy);
      if (!name) { setError('Give the link a name'); return; }
      body = { url: tidy, title: name, ...(initial?.kind === 'shortcut' ? { keys: null } : {}),
        ...(canPlatform && !locked ? { platform } : {}) };
    }
    setBusy(true); setError(null);
    try {
      await onSave({ ...body, description: description.trim() || null,
        ...((initial || pickSection) && sectionId !== section.id ? { section_id: sectionId } : {}) });
    } catch (err) {
      setError(L.said(err, 'Could not save the link')); setBusy(false);
    }
  };
  const winApps = schemes.filter((s) => (s.platform || (s.windows ? 'windows' : 'any')) === 'windows');
  const macApps = schemes.filter((s) => s.platform === 'mac');
  const preview = kind === 'shortcut' && keys.trim() ? L.parseKeys(keys) : null;
  return (
    <Modal title={initial ? 'Edit link' : 'Add a link'} subtitle={`${page?.title || ''} › ${section.title}`} onClose={onClose}
           footer={(
             <>
               <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
               <button type="submit" form="lk-link-form" className="btn btn-primary" disabled={busy}>
                 {initial ? 'Save' : 'Add link'}
               </button>
             </>
           )}>
      <form id="lk-link-form" className="lk-form" onSubmit={submit} noValidate>
        {canKeys && (
          <div className="lk-seg lk-kind-seg" role="radiogroup" aria-label="What it is">
            {[['link', 'Website or app'], ['shortcut', 'Keys to press']].map(([v, label]) => (
              <button key={v} type="button" role="radio" aria-checked={kind === v} className={kind === v ? 'is-on' : ''}
                      onClick={() => { setKind(v); setError(null); if (v === 'shortcut' && platform === 'any') setPlatform('mac'); }}>
                <LinkIcon name={v === 'link' ? 'link' : 'keyboard'} size={14} /> {label}
              </button>
            ))}
          </div>
        )}
        {kind === 'link' ? (
          <>
            <label className="lk-field">
              <span className="lk-label">Address</span>
              <input type="text" inputMode="url" autoComplete="off" spellCheck={false} value={url} maxLength={2000} autoFocus
                     onChange={(e) => setUrl(e.target.value)} placeholder="https://… or an app below" />
            </label>
            <div className="lk-apps" role="group" aria-label="Windows apps">
              <span className="lk-hint"><LinkIcon name="windows" size={12} /> Windows:</span>
              {winApps.map((a) => (
                <button key={a.scheme} type="button" className={`lk-chip${L.schemeOf(url) === a.scheme ? ' is-on' : ''}`} onClick={() => pickApp(a)}>
                  <LinkIcon name={a.scheme} size={13} /> {a.label}
                </button>
              ))}
            </div>
            {macApps.length > 0 && (
              <div className="lk-apps" role="group" aria-label="Mac apps">
                <span className="lk-hint"><LinkIcon name="mac" size={12} /> Mac:</span>
                {macApps.map((a) => (
                  <button key={a.scheme} type="button" className={`lk-chip${L.schemeOf(url) === a.scheme ? ' is-on' : ''}`} onClick={() => pickApp(a)}>
                    <LinkIcon name={a.scheme} size={13} /> {a.label}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <label className="lk-field">
            <span className="lk-label">Keys</span>
            <input type="text" autoComplete="off" spellCheck={false} value={keys} maxLength={200} autoFocus
                   onChange={(e) => setKeys(e.target.value)} placeholder='cmd+shift+5   or   cmd+space then "Calculator" then return' />
            <span className="lk-help">
              Join keys pressed together with <code>+</code>, steps with <code>then</code>, and put a word to type in quotes.
              Keys: cmd, shift, option, ctrl, space, return, a letter or a digit.
            </span>
            {preview && (
              <span className="lk-keys-preview" aria-live="polite">
                <Keys keys={keys} platform={shownOn} compact />
              </span>
            )}
          </label>
        )}
        <label className="lk-field">
          <span className="lk-label">Name</span>
          <input type="text" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)}
                 placeholder={kind === 'shortcut' ? 'e.g. Calculator' : (L.hostOf(L.tidyUrl(url)) || 'What people will see')} />
        </label>
        <label className="lk-field">
          <span className="lk-label">Description <span className="lk-optional">optional</span></span>
          <input type="text" value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)}
                 placeholder="A few words on what it is for" />
          <span className={`lk-counter${description.trim().length > 90 ? ' is-long' : ''}`}>
            {description.trim().length > 90 ? `${description.trim().length} - 90 or fewer reads best` : `${description.length}/90`}
          </span>
        </label>
        {canPlatform && (
          <fieldset className="lk-field">
            <legend className="lk-label">Show on</legend>
            <div className="lk-seg lk-show-seg" role="radiogroup" aria-label="Show on">
              {SHOW_ON.map(([v, label]) => {
                const off = (locked && v !== fixed) || (kind === 'shortcut' && v === 'any');
                return (
                  <button key={v} type="button" role="radio" aria-checked={shownOn === v} disabled={off}
                          className={shownOn === v ? 'is-on' : ''} onClick={() => setPlatform(v)}>
                    {v !== 'any' && <LinkIcon name={v === 'windows' ? 'windows' : 'mac'} size={12} />} {label}
                  </button>
                );
              })}
            </div>
            <span className="lk-help">
              {locked ? `${app.label} opens only on ${fixed === 'mac' ? 'a Mac' : 'Windows'}, so it shows only there.`
                : kind === 'shortcut' ? 'Keys are different on Windows and on a Mac, so choose one.'
                  : shownOn === 'any' ? 'Everybody sees it.'
                    : `Only people on ${shownOn === 'mac' ? 'a Mac' : 'Windows'} see it. Phones and tablets do not.`}
            </span>
          </fieldset>
        )}
        {(initial || pickSection) && (
          <label className="lk-field">
            <span className="lk-label">Section</span>
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
              {pages.map((p) => (
                <optgroup key={p.id} label={p.title}>
                  {p.sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
        )}
        {error && <p className="lk-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

/* The "delete?" dialog that lived here is now components/ConfirmDialog. */

/* ======================================================= history, deleted */

const VERB = { created: 'added', edited: 'changed', moved: 'moved', deleted: 'deleted', restored: 'restored' };
const FIELD_LABEL = { title: 'Name', url: 'Address', keys: 'Keys', description: 'Description', platform: 'Shows on', icon: 'Icon',
  nav_group: 'Group', style: 'Shown as' };
const FIELD_VALUE = { nav_group: (v) => GROUP_WORD[v] || v, style: (v) => (v === 'authority' ? 'Authority card' : v === 'list' ? 'List' : v) };
const shownValue = (f, v) => (v === null || v === undefined || v === '' ? '(none)' : (FIELD_VALUE[f] ? FIELD_VALUE[f](v) : v));
const when = (at) => new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Malta' });
const UNDO_SUFFIX = / \(undoing the reorganisation\)$/;

/** One entry's name, where it lives and what changed, for History. */
function describeEntry(e, names) {
  const row = e.after || e.before || {};
  const where = e.entity === 'link' ? [e.page_title, e.section_title].filter(Boolean).join(' › ')
    : e.entity === 'section' ? e.page_title : null;
  let detail = null;
  if (e.action === 'edited' && e.before && e.after) {
    const changed = Object.keys(FIELD_LABEL).filter((f) => (e.before[f] ?? null) !== (e.after[f] ?? null));
    detail = (
      <dl className="lk-diff">
        {changed.map((f) => (
          <div key={f} className="lk-diff-row">
            <dt>{FIELD_LABEL[f]}</dt>
            <dd><del>{shownValue(f, e.before[f])}</del><span aria-hidden="true"> → </span><span className="lk-sr"> became </span><ins>{shownValue(f, e.after[f])}</ins></dd>
          </div>
        ))}
      </dl>
    );
  } else if (e.action === 'moved' && e.before && e.after) {
    const parent = e.entity === 'link' ? 'section_id' : e.entity === 'section' ? 'page_id' : null;
    if (parent && e.before[parent] !== e.after[parent]) {
      const from = e.from_title || names[e.before[parent]] || 'elsewhere';
      const to = e.to_title || names[e.after[parent]] || 'elsewhere';
      detail = <p className="lk-log-note">From {from} to {to}</p>;
    } else if (Number.isInteger(e.before.position) && Number.isInteger(e.after.position)) {
      detail = <p className="lk-log-note">From place {e.before.position + 1} to place {e.after.position + 1}</p>;
    }
  } else if (e.entity === 'link' && (e.action === 'deleted' || e.action === 'created') && row.url) {
    detail = <p className="lk-log-note lk-log-url">{row.url}</p>;
  }
  return { name: row.title, where, detail };
}

function LogEntry({ e, names, nested = false }) {
  const d = describeEntry(e, names);
  const who = nested ? null : (e.actor_name || 'Somebody');
  return (
    <li className={`lk-log-entry is-${e.action}${nested ? ' is-nested' : ''}`}>
      {!nested && <span className={`lk-avatar lk-tone-${toneOf(e.actor_name)}`} aria-hidden="true">{initialsOf(e.actor_name || '?')}</span>}
      <div className="lk-log-body">
        <p className="lk-log-line">
          {who && <><strong>{who}</strong> </>}{nested ? `${VERB[e.action][0].toUpperCase()}${VERB[e.action].slice(1)}` : VERB[e.action]} the {e.entity} <strong>“{d.name}”</strong>
          {d.where && <span className="lk-log-where"> in {d.where}</span>}
        </p>
        {d.detail}
      </div>
      {!nested && (
        <time className="lk-log-time" dateTime={e.at} title={new Date(e.at).toLocaleString('en-GB', { timeZone: 'Europe/Malta' })}>
          {relative(e.at)}
        </time>
      )}
    </li>
  );
}

/**
 * The reorganisation (082) - or its undo - as one entry: what it did, in a
 * sentence and in numbers, and every change it made a press away, a hundred
 * at a time, in the order it made them.
 */
function RunEntry({ e, names, entity, query }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(null);
  const run = e.run || {};
  const undo = run.kind === 'undo';
  const listId = `lk-run-${e.id}`;
  const load = async (after = null) => {
    try {
      const d = await L.loadLogRun(e.id, { entity, q: query, after });
      setItems((x) => [...(after ? x || [] : []), ...d.entries]); setNext(d.next);
    } catch (err) { setError(L.said(err, 'Could not load the changes')); }
  };
  const toggle = () => { setOpen((v) => !v); if (!items) load(); };
  const parts = [run.moved && plural(run.moved, 'move'), run.edited && plural(run.edited, 'rename', 'renames and rewordings'),
    run.created && `${run.created} added`, run.restored && `${run.restored} put back`, run.deleted && `${run.deleted} deleted`].filter(Boolean);
  const what = [run.pages && plural(run.pages, 'page'), run.sections && plural(run.sections, 'section'), run.links && plural(run.links, 'link')]
    .filter(Boolean).join(', ');
  return (
    <li className={`lk-log-entry lk-log-run is-${undo ? 'undo' : 'reorg'}`}>
      <span className="lk-avatar lk-avatar-run" aria-hidden="true"><LinkIcon name={undo ? 'restore' : 'sparkle'} size={16} /></span>
      <div className="lk-log-body">
        <p className="lk-log-line">
          {undo
            ? <><strong>{(e.actor_name || 'An administrator').replace(UNDO_SUFFIX, '')}</strong> undid the reorganisation</>
            : <><strong>Reorganised by Jarvis</strong> on {when(e.at)}</>}
        </p>
        <p className="lk-log-note">
          {undo ? 'Put the pages, sections and links back as they were' : 'The links sorted into eight pages by service line, with authority cards'}
          {what ? ` - ${what}` : ''}.
        </p>
        <p className="lk-run-tally">{parts.join(' · ')}</p>
        <button type="button" className="btn btn-plain btn-small lk-run-toggle" aria-expanded={open} aria-controls={listId} onClick={toggle}>
          <LinkIcon name={open ? 'up' : 'down'} size={14} /> {open ? 'Hide the changes' : `Show all ${run.count} changes`}
        </button>
        {open && (
          <div id={listId} className="lk-run-list">
            {error && <p className="lk-error" role="alert">{error}</p>}
            {!items && !error && <p className="lk-muted">Loading…</p>}
            {items && (
              <ol className="lk-log is-nested">
                {items.map((x) => <LogEntry key={x.id} e={x} names={names} nested />)}
              </ol>
            )}
            {next && <button type="button" className="btn btn-outline btn-small lk-more" onClick={() => load(next)}>Show more</button>}
          </div>
        )}
      </div>
      <time className="lk-log-time" dateTime={e.at} title={new Date(e.at).toLocaleString('en-GB', { timeZone: 'Europe/Malta' })}>
        {relative(e.at)}
      </time>
    </li>
  );
}

function HistoryPanel({ names, reorg, onUndo, onClose }) {
  const [entity, setEntity] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let live = true;
    setEntries(null); setError(null);
    L.loadLog({ entity, q: query })
      .then((d) => { if (live) { setEntries(d.entries); setNext(d.next); } })
      .catch((e) => { if (live) setError(L.said(e, 'Could not load the history')); });
    return () => { live = false; };
  }, [entity, query]);

  const more = async () => {
    try {
      const d = await L.loadLog({ entity, q: query, before: next });
      setEntries((x) => [...(x || []), ...d.entries]); setNext(d.next);
    } catch (e) { setError(L.said(e, 'Could not load more')); }
  };

  return (
    <Modal title="History" subtitle="Every change to the links, newest first" onClose={onClose} wide>
      <div className="lk-panel-tools">
        <div className="lk-seg" role="group" aria-label="Show">
          {[['', 'Everything'], ['page', 'Pages'], ['section', 'Sections'], ['link', 'Links']].map(([v, label]) => (
            <button key={v || 'all'} type="button" className={entity === v ? 'is-on' : ''} aria-pressed={entity === v}
                    onClick={() => setEntity(v)}>{label}</button>
          ))}
        </div>
        <label className="lk-search is-small">
          <LinkIcon name="search" size={15} />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Who, or which link" aria-label="Search the history" />
        </label>
      </div>
      {reorg?.can_undo && (
        <div className="lk-reorg-bar">
          <LinkIcon name="info" size={15} />
          <p>Jarvis reorganised the links on {when(reorg.applied_at)}. As an administrator you can put them all back as they were.</p>
          <button type="button" className="btn btn-outline btn-small lk-undo-reorg" onClick={onUndo}>
            <LinkIcon name="restore" size={14} /> Undo the reorganisation
          </button>
        </div>
      )}
      {reorg?.undone_at && (
        <p className="lk-reorg-done"><LinkIcon name="restore" size={14} /> The reorganisation was undone on {when(reorg.undone_at)}{reorg.undone_by_name ? ` by ${reorg.undone_by_name}` : ''}.</p>
      )}
      {error && <p className="lk-error" role="alert">{error}</p>}
      {!entries && !error && <p className="lk-muted">Loading…</p>}
      {entries && entries.length === 0 && <p className="lk-muted">Nothing matches.</p>}
      {entries && entries.length > 0 && (
        <ol className="lk-log">
          {entries.map((e) => (e.run
            ? <RunEntry key={e.id} e={e} names={names} entity={entity} query={query} />
            : <LogEntry key={e.id} e={e} names={names} />))}
        </ol>
      )}
      {next && <button type="button" className="btn btn-outline lk-more" onClick={more}>Show older</button>}
    </Modal>
  );
}

/**
 * Undo the reorganisation (administrators; routes/links.js undoReorg): says
 * what it will do and what it will not touch, then - having done it - what it
 * put back and what it left as people had changed it.
 */
function UndoReorgDialog({ reorg, onDone, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const go = async () => {
    setBusy(true); setError(null);
    try { const r = await L.undoReorg(); setResult(r); onDone(r); } catch (e) {
      setError(L.said(e, 'Could not undo the reorganisation'));
    }
    setBusy(false);
  };
  if (result) {
    const put = [result.moved && plural(result.moved, 'link and section', 'links and sections') + ' moved back',
      result.edited && `${plural(result.edited, 'name')} put back`, result.restored && `${result.restored} restored`,
      result.removed && plural(result.removed, 'new section') + ' removed'].filter(Boolean);
    return (
      <Modal title="The reorganisation is undone" onClose={onClose}
             footer={<button type="button" className="btn btn-primary" onClick={onClose}>Done</button>}>
        <div className="lk-confirm lk-undo-result" role="status">
          <p>{put.length ? `${put.join(', ')}.` : 'Nothing needed putting back.'}</p>
          {result.skipped.length > 0 ? (
            <>
              <p><strong>Left as people changed {result.skipped.length === 1 ? 'it' : 'them'}:</strong></p>
              <ul className="lk-undo-kept">
                {result.skipped.map((s, i) => (
                  <li key={i}><span className={`lk-kind is-${s.entity}`}>{s.entity}</span> <strong>{s.title}</strong> - {s.why}</li>
                ))}
              </ul>
            </>
          ) : <p className="lk-muted">Nobody had changed anything since, so it is exactly as it was.</p>}
        </div>
      </Modal>
    );
  }
  return (
    <Modal title="Undo the reorganisation?" onClose={onClose}
           footer={(
             <>
               <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
               <button type="button" className="btn btn-danger" disabled={busy} onClick={go}>
                 {busy ? 'Undoing…' : 'Undo the reorganisation'}
               </button>
             </>
           )}>
      <div className="lk-confirm">
        <p>
          Every page, section and link goes back to where it was before Jarvis reorganised the links
          {reorg?.applied_at ? ` on ${when(reorg.applied_at)}` : ''}: the old pages and sections, their names and their order.
          It is the same for everybody.
        </p>
        <p>Anything somebody has changed since - a link renamed, moved or deleted, a link added to one of the new sections - stays as they left it, and you will see a list.</p>
        <p className="lk-muted">It can be done once, and every step is in History.</p>
        {error && <p className="lk-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

function TrashPanel({ onClose, onRestored }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    L.loadTrash().then(setItems).catch((e) => setError(L.said(e, 'Could not load what was deleted')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const bring = async (item) => {
    setBusy(item.id); setError(null);
    try {
      const r = await L.restore(item.entity, item.id);
      setItems((x) => x.filter((i) => i.id !== item.id));
      onRestored(r);
      load();
    } catch (e) { setError(L.said(e, 'Could not restore that')); }
    setBusy(null);
  };

  return (
    <Modal title="Recently deleted" subtitle="Restore puts it back where it was" onClose={onClose} wide>
      {error && <p className="lk-error" role="alert">{error}</p>}
      {!items && !error && <p className="lk-muted">Loading…</p>}
      {items && items.length === 0 && (
        <div className="lk-empty">
          <LinkIcon name="bin" size={28} />
          <p>Nothing has been deleted.</p>
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="lk-trash">
          {items.map((i) => {
            const where = i.entity === 'link' ? [i.page_title, i.section_title].filter(Boolean).join(' › ')
              : i.entity === 'section' ? i.page_title : null;
            const inside = i.entity === 'page' ? [i.sections && plural(i.sections, 'section'), i.links && plural(i.links, 'link')].filter(Boolean)
              : i.entity === 'section' && i.links ? [plural(i.links, 'link')] : [];
            return (
              <li key={`${i.entity}-${i.id}`} className="lk-trash-item">
                <span className={`lk-kind is-${i.entity}`}>{i.entity}</span>
                <div className="lk-trash-text">
                  <p className="lk-trash-title">{i.title}</p>
                  <p className="lk-trash-meta">
                    {where && <>From {where} · </>}
                    {inside.length > 0 && <>With {inside.join(' and ')} · </>}
                    Deleted {relative(i.deleted_at)}{i.deleted_by_name ? ` by ${i.deleted_by_name}` : ''}
                  </p>
                  {i.parent_deleted && (
                    <p className="lk-trash-note">Its {i.entity === 'link' ? 'section or page' : 'page'} is deleted too - restore that first.</p>
                  )}
                </div>
                <button type="button" className="btn btn-secondary btn-small" disabled={busy === i.id || i.parent_deleted}
                        onClick={() => bring(i)} aria-label={`Restore ${i.title}`}>
                  <LinkIcon name="restore" size={14} /> Restore
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

/* =================================================================== page */

/** The line under a page that has links for other computers. */
function awayLine(h, device) {
  if (!h) return null;
  const win = h.windows || 0; const mac = h.mac || 0;
  if (!win && !mac) return null;
  const are = (n) => (n === 1 ? 'is' : 'are');
  if (device === 'mac') return `${plural(win, 'link')} for Windows ${are(win)} not shown on a Mac.`;
  if (device === 'windows') return `${plural(mac, 'link')} for Macs ${are(mac)} not shown on Windows.`;
  const n = win + mac;
  const which = win && mac ? 'Windows and Mac computers' : win ? 'Windows computers' : 'Macs';
  return `${plural(n, 'link')} for ${which} ${are(n)} not shown on this device.`;
}

export default function LinksPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | not-ready | error
  const [problem, setProblem] = useState(null);
  const [params, setParams] = useSearchParams();
  // ?q= - the global search's "See all in Links" - starts the search with its words.
  const [query, setQuery] = useState(() => params.get('q') || '');
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [panel, setPanel] = useState(null);
  const [toast, setToast] = useState(null);
  const [menu, setMenu] = useState(null);
  const [pop, setPop] = useState(null);
  const [density, setDensity] = useState(savedDensity);
  const toastTimer = useRef(null);
  const refocus = useRef(null);
  const searchRef = useRef(null);
  const device = useMemo(() => L.deviceOf(), []);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState((s) => (s === 'ready' ? s : 'loading'));
    try {
      const d = await L.loadLinks();
      setData(d); setState('ready');
    } catch (e) {
      if (L.notReady(e)) setState('not-ready');
      else { setProblem(L.said(e, 'Could not load the links')); setState('error'); }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // View as a contractor shows what the contractor sees: no editing.
  const canEdit = Boolean(data?.can_edit) && !storedViewAsContractorId();
  useEffect(() => { if (!canEdit) setEditing(false); }, [canEdit]);

  const allPages = useMemo(() => data?.pages || [], [data]);
  const schemes = useMemo(() => data?.app_schemes || [], [data]);

  /*
   * What this computer is shown: a Windows link on Windows, a Mac link on a
   * Mac, and on anything else neither. A section that had links and has none
   * left here goes, and so does a page. Editing shows everything - the same
   * arrays, so a drag's indexes are the server's.
   */
  const { pages, hidden } = useMemo(() => {
    if (editing) return { pages: allPages, hidden: {} };
    const away = {};
    const shown = [];
    for (const p of allPages) {
      const h = { windows: 0, mac: 0 };
      let had = false;
      const sections = [];
      for (const s of p.sections) {
        if (s.links.length) had = true;
        const links = s.links.filter((l) => {
          const where = L.platformOf(l, schemes);
          if (where === 'any' || where === device) return true;
          h[where] = (h[where] || 0) + 1;
          return false;
        });
        if (links.length || !s.links.length) sections.push(links.length === s.links.length ? s : { ...s, links });
      }
      away[p.id] = h;
      if (had && !sections.some((s) => s.links.length)) continue;
      shown.push(sections.length === p.sections.length && sections.every((s, i) => s === p.sections[i]) ? p : { ...p, sections });
    }
    return { pages: shown, hidden: away };
  }, [allPages, schemes, device, editing]);
  // A bookmark of a page 082 retired opens the page its links went to -
  // unless somebody has restored the old page, which then opens as itself.
  const asked = params.get('page') || remembered();
  const wanted = asked && RETIRED_SLUGS[asked] && !allPages.some((p) => p.slug === asked) ? RETIRED_SLUGS[asked] : asked;
  const choose = (p) => {
    remember(p.slug);
    setParams((x) => { const n = new URLSearchParams(x); n.set('page', p.slug); return n; }, { replace: true });
  };
  // ...and the address bar then says where it really is.
  const aliased = state === 'ready' && asked !== wanted ? wanted : null;
  useEffect(() => {
    if (!aliased) return;
    remember(aliased);
    if (params.get('page')) setParams((x) => { const n = new URLSearchParams(x); n.set('page', aliased); return n; }, { replace: true });
  }, [aliased]); // eslint-disable-line react-hooks/exhaustive-deps

  // "/" to search, as on most sites; not while typing somewhere else.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return;
      if (document.querySelector('.md-backdrop')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // After a keyboard move, the handle that moved keeps the focus.
  useEffect(() => {
    if (!refocus.current) return;
    const el = document.querySelector(`[data-handle="${refocus.current}"]`);
    refocus.current = null;
    if (el) el.focus();
  });

  const say = useCallback((message, { undo = null, error = false } = {}) => {
    clearTimeout(toastTimer.current);
    setToast({ message, undo, error, key: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), undo ? UNDO_MS : 4500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /*
   * Favourites, Most used and Today's rates (migration 076, LinksExtras.js).
   * Favourites is a page of its own at the top of the list and where a first
   * visit lands; before 076 there is none of it and this is the page it was.
   * Both lists are read through `pages`, so a link this computer is not shown
   * is not shown there either.
   */
  const extras = useLinksExtras(say);
  const favView = extras.status !== 'off' && (wanted === FAV_SLUG || !wanted);
  // A page that is all Mac (or all Windows) apps is not in the list on the
  // other kind of computer; asked for by name, it says why it is empty.
  const awayPage = !favView && wanted && !pages.some((p) => p.slug === wanted)
    ? allPages.find((p) => p.slug === wanted) || null : null;
  const current = favView || awayPage ? null : pages.find((p) => p.slug === wanted) || pages[0] || null;
  const byId = useMemo(() => indexLinks(pages), [pages]);
  /**
   * A link out of its card, in Favourites and Most used: an authority card's
   * link by the card's name and its own ("FIAU · Implementing Procedures"),
   * anything else with its page and section under it.
   */
  const outOfCard = useCallback((x) => {
    const auth = x.section.style === 'authority';
    const named = auth && x.link.title !== x.section.title && !x.link.title.startsWith(x.section.title);
    return { link: x.link, name: named ? `${x.section.title} · ${x.link.title}` : null,
      where: auth ? x.page.title : (x.page ? `${x.page.title} › ${x.section.title}` : x.section.title) };
  }, []);
  const favShown = useMemo(() => extras.favourites.map((id) => byId.get(id)).filter(Boolean).map(outOfCard),
    [extras.favourites, byId, outOfCard]);
  const usedShown = useMemo(() => extras.mostUsed.map((u) => byId.get(u.id) && { ...u, ...outOfCard(byId.get(u.id)) })
    .filter(Boolean).slice(0, 8), [extras.mostUsed, byId, outOfCard]);
  /** An open, from a tile, a card or a chip (click, or middle-click for a new tab): counted for Most used. */
  const countOpen = (e) => {
    if (editing || (e.type === 'auxclick' && e.button !== 1)) return;
    const id = e.target.closest?.('.lk-tile-main, .lk-auth-main, .lk-chip-link')?.closest('[data-link-id]')?.getAttribute('data-link-id');
    if (id) extras.record(id);
  };

  /** The right-click menu; closing it from the keyboard puts the focus back on its link. */
  const menuRef = useRef(null);
  menuRef.current = menu;
  const closeMenu = useCallback((refocusLink) => {
    const from = menuRef.current?.from;
    setMenu(null);
    if (refocusLink && from?.isConnected) from.focus();
  }, []);
  // On a phone the pages are a strip that scrolls sideways - ten of them do
  // not fit - so the open one is brought into the strip's view, not left
  // off its edge.
  const currentId = current?.id;
  useEffect(() => {
    const strip = document.querySelector('.lk-nav');
    const el = strip?.querySelector('.lk-page-item.is-current');
    if (!strip || !el || strip.scrollWidth <= strip.clientWidth) return;
    const s = strip.getBoundingClientRect(); const r = el.getBoundingClientRect();
    if (r.left < s.left || r.right > s.right) strip.scrollLeft += r.left - s.left - (s.width - r.width) / 2;
  }, [currentId, state]);

  /** The "how to open" card; closing it from the keyboard puts the focus back on its tile. */
  const popRef = useRef(null);
  popRef.current = pop;
  const closePop = useCallback((refocusTile) => {
    const from = popRef.current?.from;
    setPop(null);
    if (refocusTile && from?.isConnected) from.focus();
  }, []);
  useEffect(() => { if (editing) setPop(null); }, [editing]);
  const copied = useCallback((ok) => say(ok ? 'Link address copied' : 'Could not copy - your browser blocked it', { error: !ok }), [say]);

  /** Change the local copy, then the server; on a refusal, reload and say so. */
  const attempt = async (change, call, failMsg) => {
    if (change) setData((d) => (d ? { ...d, pages: change(d.pages) } : d));
    try { return await call(); } catch (e) {
      say(L.said(e, failMsg), { error: true });
      load(true);
      return null;
    }
  };

  const move = (arr, from, to) => { const a = [...arr]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; };

  const onMove = (group, from, to, id = null) => {
    if (id) refocus.current = id;
    if (group === 'pages') {
      const p = pages[from];
      attempt((ps) => move(ps, from, to), () => L.changePage(p.id, { position: to }), 'Could not move that page');
    } else if (group === 'sections') {
      const s = current.sections[from];
      attempt((ps) => ps.map((p) => (p.id === current.id ? { ...p, sections: move(p.sections, from, to) } : p)),
        () => L.changeSection(s.id, { position: to }), 'Could not move that section');
    } else if (group.startsWith('links:')) {
      const sid = group.slice(6);
      const sec = current.sections.find((s) => s.id === sid);
      const l = sec.links[from];
      attempt((ps) => ps.map((p) => ({ ...p, sections: p.sections.map((s) => (s.id === sid ? { ...s, links: move(s.links, from, to) } : s)) })),
        () => L.changeLink(l.id, { position: to }), 'Could not move that link');
    } else if (group === 'favourites') {
      extras.move(favShown.map((f) => f.link.id), from, to);
    }
  };
  const drag = useDragOrder((group, from, to) => onMove(group, from, to));

  const undoable = (what, title, entity, id) => say(`Deleted ${what} “${title}”`, {
    undo: async () => {
      setToast(null);
      try { await L.restore(entity, id); await load(true); say(`Restored “${title}”`); } catch (e) {
        say(L.said(e, 'Could not undo that'), { error: true });
      }
    }
  });

  /** Everything a section card and the heading can ask for. */
  const act = {
    addPage: () => setDialog({ kind: 'page' }),
    renamePage: (p) => setDialog({ kind: 'page', initial: p }),
    deletePage: (p) => setDialog({ kind: 'confirm-page', page: p }),
    addSection: () => setDialog({ kind: 'section' }),
    renameSection: (s) => setDialog({ kind: 'section', initial: s }),
    deleteSection: (s) => (s.links.length ? setDialog({ kind: 'confirm-section', section: s }) : act.reallyDeleteSection(s)),
    addLink: (s) => setDialog({ kind: 'link', section: s }),
    editLink: (s, l) => setDialog({ kind: 'link', section: s, initial: l }),
    deleteLink: async (l) => {
      const r = await attempt((ps) => ps.map((p) => ({ ...p, sections: p.sections.map((s) => ({ ...s, links: s.links.filter((x) => x.id !== l.id) })) })),
        () => L.deleteLink(l.id), 'Could not delete that link');
      if (r) undoable('the link', l.title, 'link', l.id);
    },
    reallyDeleteSection: async (s) => {
      setDialog(null);
      const r = await attempt((ps) => ps.map((p) => ({ ...p, sections: p.sections.filter((x) => x.id !== s.id) })),
        () => L.deleteSection(s.id), 'Could not delete that section');
      if (r) undoable('the section', s.title, 'section', s.id);
    },
    reallyDeletePage: async (p) => {
      setDialog(null);
      const r = await attempt((ps) => ps.filter((x) => x.id !== p.id), () => L.deletePage(p.id), 'Could not delete that page');
      if (r) undoable('the page', p.title, 'page', p.id);
    }
  };

  /**
   * A page put under a heading goes in with that heading's pages - after the
   * last of them - rather than at the bottom of the list under another.
   */
  const placeInGroup = async (page, group) => {
    if (!group) return;
    const others = allPages.filter((x) => x.id !== page.id);
    const last = others.reduce((at, x, i) => (x.nav_group === group ? i : at), -1);
    if (last < 0) return;
    if (allPages.findIndex((x) => x.id === page.id) !== last + 1) await L.changePage(page.id, { position: last + 1 });
  };

  const saveDialog = async (body) => {
    const d = dialog;
    if (d.kind === 'page' && d.initial) {
      await L.changePage(d.initial.id, body);
      if (body.nav_group !== undefined && body.nav_group !== (d.initial.nav_group || null)) await placeInGroup(d.initial, body.nav_group);
    } else if (d.kind === 'page') {
      const p = await L.addPage(body);
      if (body.nav_group) await placeInGroup(p, body.nav_group);
      choose(p);
    } else if (d.kind === 'section' && d.initial) await L.changeSection(d.initial.id, body);
    else if (d.kind === 'section') await L.addSection(current.id, body);
    else if (d.kind === 'link' && d.initial) await L.changeLink(d.initial.id, body);
    else if (d.kind === 'link') {
      const { section_id: into, ...rest } = body;
      await L.addLink(into || d.section.id, rest);
    }
    setDialog(null);
    await load(true);
    if (d.kind === 'link') say(d.initial ? `Saved “${body.title}”` : `Added “${body.title}”`);
  };

  /** Ids to names, for History's "moved from X to Y". */
  const names = useMemo(() => {
    const m = {};
    for (const p of allPages) {
      m[p.id] = p.title;
      for (const s of p.sections) m[s.id] = `${p.title} › ${s.title}`;
    }
    return m;
  }, [allPages]);

  /* ---- search, across every page */
  // A section's name and its line count too, so "FIAU" brings the whole FIAU
  // card; and the names people use for things (CFR for the MTCA...) find them.
  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return null;
    const terms = searchTerms(q.replace(/ – /g, ' - '));
    const out = [];
    for (const p of pages) {
      for (const s of p.sections) {
        const hits = s.links.filter((l) => {
          const hay = `${l.title} ${l.description || ''} ${L.hostOf(l.url)} ${l.url} ${s.title} ${s.description || ''} ${p.title}`.toLowerCase();
          return terms.every((alts) => alts.some((w) => hay.includes(w)));
        });
        if (hits.length) out.push({ page: p, section: s, links: hits });
      }
    }
    return out;
  }, [q, pages]);
  const hitCount = results ? results.reduce((n, r) => n + r.links.length, 0) : 0;

  /* ---- render */
  const header = (
    <div className="page-header lk-head">
      <div>
        <h1 className="page-title">Links</h1>
        <p className="page-description">Websites and apps the firm uses, sorted by what they are for.</p>
      </div>
      {state === 'ready' && (
        <div className="lk-head-tools">
          <label className="lk-search">
            <LinkIcon name="search" size={16} />
            <input ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
                   placeholder="Search every page" aria-label="Search the links" />
            {!query && <kbd className="lk-kbd" aria-hidden="true">/</kbd>}
          </label>
          <div className="lk-seg lk-density" role="group" aria-label="How much to show">
            {[['comfortable', 'Comfortable - names and descriptions'], ['compact', 'Compact - names only, two to a row']].map(([v, label]) => (
              <button key={v} type="button" className={density === v ? 'is-on' : ''} aria-pressed={density === v}
                      aria-label={label.split(' - ')[0]} title={label}
                      onClick={() => { setDensity(v); saveDensity(v); }}>
                <LinkIcon name={v} size={15} />
              </button>
            ))}
          </div>
          {canEdit && (
            <>
              <button type="button" className="btn btn-outline lk-tool-btn" onClick={() => setPanel('history')} aria-label="History">
                <LinkIcon name="history" /> <span className="lk-tool-word">History</span>
              </button>
              <button type="button" className="btn btn-outline lk-tool-btn" onClick={() => setPanel('trash')} aria-label="Recently deleted">
                <LinkIcon name="bin" /> <span className="lk-tool-word">Deleted</span>
              </button>
              <button type="button" className={`btn ${editing ? 'btn-primary' : 'btn-secondary'} lk-tool-btn lk-edit-toggle`}
                      aria-pressed={editing} onClick={() => setEditing((v) => !v)}>
                <LinkIcon name={editing ? 'check' : 'pencil'} /> {editing ? 'Done' : 'Edit'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (state === 'loading') {
    return (
      <div className="container lk-page">
        {header}
        <div className="lk-layout" aria-busy="true">
          <div className="lk-nav lk-skeleton-nav">{[0, 1, 2, 3].map((i) => <span key={i} className="lk-skel lk-skel-row" />)}</div>
          <div className="lk-grid">{[0, 1, 2].map((i) => <div key={i} className="lk-card lk-skel lk-skel-card" />)}</div>
        </div>
      </div>
    );
  }
  if (state === 'not-ready' || state === 'error') {
    return (
      <div className="container lk-page">
        {header}
        <div className="lk-empty lk-empty-big">
          <LinkIcon name="link" size={32} />
          {state === 'not-ready' ? (
            <>
              <p className="lk-empty-title">Links are not set up on this server yet</p>
              <p>An administrator needs to run migration 070 (<code>npm run migrate:070</code>).</p>
            </>
          ) : (
            <>
              <p className="lk-empty-title">{problem}</p>
              <button type="button" className="btn btn-outline" onClick={() => load()}>Try again</button>
            </>
          )}
        </div>
      </div>
    );
  }

  const sections = current?.sections || [];
  const linkCount = (p) => p.sections.reduce((n, s) => n + s.links.length, 0);
  // The left nav's captions (082): only once some page has a group.
  const grouped = pages.some((p) => p.nav_group);
  const more = { sections: Boolean(data?.features?.sections), groups: Boolean(data?.features?.groups),
    defaultGroup: current?.nav_group || '' };
  const compact = density === 'compact' && !editing;
  const awayWhy = (() => {
    if (!awayPage) return null;
    const ps = new Set(awayPage.sections.flatMap((s) => s.links.map((l) => L.platformOf(l, schemes))));
    if (ps.size === 1 && ps.has('mac')) return 'These are all Mac apps – open Jarvis on a Mac to use them.';
    if (ps.size === 1 && ps.has('windows')) return 'These are all Windows apps – open Jarvis on a Windows computer to use them.';
    return 'Nothing on this page opens on this device – the apps here are for Windows or a Mac.';
  })();

  return (
    <MenuContext.Provider value={editing ? null : setMenu}>
    <PopContext.Provider value={editing ? null : setPop}>
    <ExtrasContext.Provider value={extras.ctx}>
    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- counts opens of the links inside; Enter on a link is a click */}
    <div className={`container lk-page${editing ? ' is-editing' : ''}${compact ? ' is-compact' : ''}`} onClick={countOpen} onAuxClick={countOpen}>
      {header}

      {editing && (
        <div className="lk-edit-banner" role="status">
          <LinkIcon name="pencil" size={15} />
          <span>Editing - drag <Grip /> to reorder, or focus it and use the arrow keys. Deleting can be undone.</span>
        </div>
      )}

      <div className="lk-layout">
        <aside className="lk-side">
        <nav className={`lk-nav${grouped ? ' is-grouped' : ''}`} aria-label="Link pages">
          {extras.status !== 'off' && (
            <ul className="lk-pages lk-pages-pinned">
              <li className={`lk-page-item${favView ? ' is-current' : ''}`}>
                <button type="button" className="lk-page-btn" aria-current={favView && !results ? 'page' : undefined}
                        onClick={() => { setQuery(''); choose({ slug: FAV_SLUG }); }}>
                  <span className="lk-page-icon is-star"><StarGlyph filled={favView} size={16} /></span>
                  <span className="lk-page-name">Favourites</span>
                  {extras.status === 'ready' && <span className="lk-page-count">{favShown.length}</span>}
                </button>
              </li>
            </ul>
          )}
          <ul className="lk-pages" data-lk-order="">
            {pages.map((p, i) => {
              const short = grouped ? shortName(p.title) : p.title;
              const caption = grouped && (i === 0 || (pages[i - 1].nav_group || '') !== (p.nav_group || ''));
              return (
                <React.Fragment key={p.id}>
                  {caption && (
                    <li className={`lk-nav-cap${i === 0 ? ' is-first' : ''}`} role="presentation">
                      <span className="lk-nav-cap-text">{GROUP_WORD[p.nav_group] || 'More'}</span>
                    </li>
                  )}
                  <li data-lk-item="" className={`lk-page-item${p.id === current?.id ? ' is-current' : ''}${drag.markOf('pages', i, pages.length)}`}>
                    {editing && !results && <Handle label={p.title} group="pages" index={i} total={pages.length} drag={drag} onMove={onMove} id={p.id} />}
                    <button type="button" className="lk-page-btn" aria-current={p.id === current?.id && !results ? 'page' : undefined}
                            onClick={() => { setQuery(''); choose(p); }}>
                      <span className="lk-page-icon"><LinkIcon name={p.icon || 'link'} size={17} /></span>
                      <span className={`lk-page-name${short !== p.title ? ' has-short' : ''}`}>{p.title}</span>
                      {short !== p.title && <span className="lk-page-short" aria-hidden="true">{short}</span>}
                      <span className="lk-page-count">{linkCount(p)}</span>
                    </button>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
          {editing && (
            <button type="button" className="lk-add-row lk-add-page" onClick={act.addPage}>
              <LinkIcon name="plus" /> Add a page
            </button>
          )}
        </nav>
        {extras.rates && <RatesCard data={extras.rates} onPin={extras.pin} say={say} />}
        </aside>

        <div className="lk-main">
          {results ? (
            <>
              <div className="lk-main-head">
                <h2 className="lk-main-title">{hitCount ? `${plural(hitCount, 'result')} for “${query.trim()}”` : `Nothing matches “${query.trim()}”`}</h2>
                <button type="button" className="btn btn-plain" onClick={() => setQuery('')}>Clear search</button>
              </div>
              <RatesHit query={query} data={extras.rates} />
              {hitCount === 0 && (
                <div className="lk-empty">
                  <LinkIcon name="search" size={28} />
                  <p>Nothing matches “{query.trim()}” – try fewer words, or the authority’s name (FIAU, MTCA, MBR).</p>
                  {canEdit && (pages[0]?.sections?.length > 0) && (
                    <button type="button" className="btn btn-secondary"
                            onClick={() => setDialog({ kind: 'link', section: (current || pages[0]).sections[0] || pages[0].sections[0],
                              preset: { title: query.trim() }, pickSection: true })}>
                      <LinkIcon name="plus" size={14} /> Add a link
                    </button>
                  )}
                </div>
              )}
              <div className="lk-grid is-flow">
                {results.map((r) => (
                  <section key={r.section.id} className="lk-card" aria-label={`${r.page.title} › ${r.section.title}`}>
                    <header className="lk-card-head">
                      <h3 className="lk-card-title">
                        <span className="lk-crumb">{dash(r.page.title)} ›</span> {dash(r.section.title)}
                      </h3>
                      <span className="lk-count">{r.links.length}</span>
                    </header>
                    {r.section.description && <p className="lk-card-desc" title={dash(r.section.description)}>{dash(r.section.description)}</p>}
                    <ul className="lk-tiles">
                      {r.links.map((l) => <Tile key={l.id} link={l} schemes={schemes} editing={false} />)}
                    </ul>
                  </section>
                ))}
              </div>
            </>
          ) : favView ? (
            <FavouritesView favourites={favShown} mostUsed={usedShown} status={extras.status} days={extras.days}
                            showCounts={canEdit} markOf={drag.markOf}
                            renderTile={(link, where, name) => <Tile link={link} schemes={schemes} editing={false} where={where} name={name} />}
                            renderHandle={(link, i, n) => (
                              <Handle label={link.title} group="favourites" index={i} total={n} drag={drag} onMove={onMove} id={`fav-${link.id}`} />
                            )} />
          ) : awayPage ? (
            <>
              <div className="lk-main-head">
                <h2 className="lk-main-title">
                  <span className="lk-main-icon"><LinkIcon name={awayPage.icon || 'link'} size={20} /></span>
                  {dash(awayPage.title)}
                </h2>
              </div>
              <div className="lk-empty">
                <LinkIcon name={awayWhy.includes('Mac') && !awayWhy.includes('Windows') ? 'mac' : 'windows'} size={28} />
                <p>{awayWhy}</p>
              </div>
            </>
          ) : current ? (
            <>
              <div className="lk-main-head">
                <h2 className="lk-main-title">
                  <span className="lk-main-icon"><LinkIcon name={current.icon || 'link'} size={20} /></span>
                  {editing ? current.title : dash(current.title)}
                </h2>
                {editing && (
                  <span className="lk-main-tools">
                    <button type="button" className="btn btn-outline btn-small" onClick={() => act.renamePage(current)}>
                      <LinkIcon name="pencil" size={14} /> Rename page
                    </button>
                    <button type="button" className="btn btn-danger btn-small" onClick={() => act.deletePage(current)}>
                      <LinkIcon name="trash" size={14} /> Delete page
                    </button>
                  </span>
                )}
              </div>
              {sections.length === 0 && !editing && (
                <div className="lk-empty">
                  <LinkIcon name="folder" size={28} />
                  <p>This page has no links yet.</p>
                  {canEdit && <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>Add some</button>}
                </div>
              )}
              <div className={`lk-grid${editing ? '' : ' is-flow'}`} data-lk-order="">
                {sections.map((s, i) => (
                  <SectionCard key={s.id} section={s} index={i} total={sections.length} editing={editing}
                               drag={drag} onMove={onMove} schemes={schemes} act={act} />
                ))}
                {editing && (
                  <button type="button" className="lk-card lk-add-card" onClick={act.addSection}>
                    <LinkIcon name="plus" size={20} />
                    <span>Add a section</span>
                  </button>
                )}
              </div>
              {awayLine(hidden[current.id], device) && (
                <p className="lk-footnote"><LinkIcon name="info" size={13} /> {awayLine(hidden[current.id], device)}</p>
              )}
            </>
          ) : (
            <div className="lk-empty">
              <LinkIcon name="link" size={28} />
              <p>There are no pages yet.</p>
              {canEdit && <button type="button" className="btn btn-primary" onClick={() => { setEditing(true); act.addPage(); }}>Add the first page</button>}
            </div>
          )}
        </div>
      </div>

      {dialog?.kind === 'page' && (
        <NameDialog what="page" withIcon initial={dialog.initial} more={more} onSave={saveDialog} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'section' && (
        <NameDialog what="section" initial={dialog.initial} more={more} onSave={saveDialog} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'link' && (
        <LinkDialog initial={dialog.initial} preset={dialog.preset} pickSection={Boolean(dialog.pickSection)}
                    section={dialog.section} pages={allPages} schemes={schemes} features={data?.features}
                    onSave={saveDialog} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'confirm-page' && (
        <ConfirmDialog title={`Delete “${dialog.page.title}”?`} confirmLabel="Delete page" danger
                       onConfirm={() => act.reallyDeletePage(dialog.page)} onCancel={() => setDialog(null)}>
          <p>
            The page goes for everybody, with its {plural(dialog.page.sections.length, 'section')} and {plural(linkCount(dialog.page), 'link')}.
          </p>
          <p className="lk-muted">You can undo it straight away, or restore it later from Recently deleted.</p>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'confirm-section' && (
        <ConfirmDialog title={`Delete “${dialog.section.title}”?`} confirmLabel="Delete section" danger
                       onConfirm={() => act.reallyDeleteSection(dialog.section)} onCancel={() => setDialog(null)}>
          <p>The section goes for everybody, with its {plural(dialog.section.links.length, 'link')}.</p>
          <p className="lk-muted">You can undo it straight away, or restore it later from Recently deleted.</p>
        </ConfirmDialog>
      )}

      {panel === 'history' && (
        <HistoryPanel names={names} reorg={data?.reorg} onUndo={() => setPanel('undo-reorg')} onClose={() => setPanel(null)} />
      )}
      {panel === 'undo-reorg' && (
        <UndoReorgDialog reorg={data?.reorg} onClose={() => setPanel(null)}
                         onDone={() => { setEditing(false); load(true); }} />
      )}
      {panel === 'trash' && (
        <TrashPanel onClose={() => setPanel(null)}
                    onRestored={(r) => { load(true); say(`Restored “${r.title}”`); }} />
      )}

      {toast && (
        <div key={toast.key} className={`lk-toast${toast.error ? ' is-error' : ''}`} role={toast.error ? 'alert' : 'status'}>
          <span className="lk-toast-text">{toast.message}</span>
          {toast.undo && <button type="button" className="lk-toast-undo" onClick={toast.undo}>Undo</button>}
          <button type="button" className="lk-toast-close" aria-label="Dismiss" onClick={() => setToast(null)}>
            <LinkIcon name="close" size={14} />
          </button>
        </div>
      )}

      {menu && <LinkMenu menu={menu} onClose={closeMenu} onCopied={copied} />}
      {pop && <ShortcutPop pop={pop} onClose={closePop} />}
    </div>
    </ExtrasContext.Provider>
    </PopContext.Provider>
    </MenuContext.Provider>
  );
}
